import CommonModal from 'components/common/CommonModal';
import ScreenLayout from 'components/common/ScreenLayout';
import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import {colors} from 'styles/colors';
import {minmax} from 'utils/minmax';
import DefaultText from 'components/common/DefaultText';
import Pagination from 'components/common/Pagination';
import moment from 'moment-timezone';
import {useNavigate} from 'react-router-dom';
import {useRecoilValue} from 'recoil';
import {localeState, userInfoState} from 'utils/Atoms';
import {
  addConstructionType,
  changeBusinessRegisterNumberImg,
  searchBackOfficeContractor,
  searchBackOfficeTongPlace,
} from 'function/api';
import {convertPdfToImage, convertTotalPage} from 'function/converter';
import {changeLocaleType, formatPhoneNumber} from 'utils/fomatPhoneNumber';
import {CountryCode} from 'libphonenumber-js';
import {formatBusinessRegisterNumber} from 'function/formatter';
import BusinessImgModal from 'components/main/BusinessImgModal';
import {allowStateStatus, siteProcessingStatus} from 'data/status';
import ChangeStateModal from 'components/main/ChangeStateModal';
import {
  ResponseSearchBackOfficeContractor,
  ResponseSearchBackOfficeTongPlace,
} from 'interfaces/interface';
import FileInputButton from 'components/common/FileInputButton';
import DefaultButton from 'components/common/DefaultButton';
import LabelInput from 'components/common/LabelInput';

/**
 * 메인 페이지 컴포넌트
 * 시공사 및 현장 목록을 관리하는 메인 페이지입니다.
 * @returns {JSX.Element} 메인 페이지 컴포넌트
 */
const Main = () => {
  // locale
  const locale = useRecoilValue(localeState);
  const [isLoading, setIsLoading] = useState(false);
  const [errorModal, setErrorModal] = useState({});

  const navigate = useNavigate();

  const userInfo = useRecoilValue(userInfoState);

  const [contractorCurrentPage, setContractorCurrentPage] = useState(1);
  const [contractorTotalPage, setContractorTotalPage] = useState(1);

  const [tongCurrentPage, setTongCurrentPage] = useState(1);
  const [tongTotalPage, setTongTotalPage] = useState(1);

  const [contractorData, setContractorData] =
    useState<null | ResponseSearchBackOfficeContractor>(null);
  const [tongData, setTongData] =
    useState<null | ResponseSearchBackOfficeTongPlace>(null);

  const [modal, setModal] = useState('');

  const [selectItem, setSelectItem] = useState<null | any>(null);

  const [addTypeOfWorkModal, setAddTypeOfWorkModal] = useState({
    uuid: '',
    type: '',
  });
  const [typeOfWorkSheetData, setTypeOfWorkSheetData] = useState<{
    name: string;
    file: File | undefined;
  }>({
    name: '',
    file: undefined,
  });
  const [addTypeOfWorkWarningModal, setAddTypeOfWorkWarningModal] =
    useState(false);

  const modalData: {[key: string]: any} = {
    // 본사시스템 서비스 준비중
    servicePreparing: {
      title: 'servicePreparing',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
      },
    },
    changeBusinessRegisterNumberImg: {
      title: '사업자등록증 변경 성공',
      detail: '사업자등록증 변경이 완료되었습니다!',
      firstButtonText: 'confirm',
      firstButtonClick: () => {
        setModal('');
        window.location.reload();
      },
    },
  };

  useEffect(() => {
    if (!userInfo?.accessToken) {
      navigate('/');
    }
  }, []);

  // 현장 리스트 - 사용X / 임시 복원
  useEffect(() => {
    if (userInfo?.accessToken) {
      searchBackOfficeTongPlace({
        userInfo,
        setErrorModal,
        setIsLoading,
        page: tongCurrentPage,
      }).then(result => {
        if (result) {
          setTongData(result);

          const resTotalPage = convertTotalPage(result.totalCount ?? 0, 10);
          setTongTotalPage(resTotalPage);
        }
      });
    }
  }, [userInfo, tongCurrentPage]);

  useEffect(() => {
    if (userInfo?.accessToken) {
      searchBackOfficeContractor({
        userInfo,
        setErrorModal,
        setIsLoading,
        page: contractorCurrentPage,
      }).then(result => {
        if (result) {
          setContractorData(result);

          const resTotalPage = convertTotalPage(result.totalCount ?? 0, 10);
          setContractorTotalPage(resTotalPage);
        }
      });
    }
  }, [userInfo, contractorCurrentPage]);

  /**
   * 작업 공종을 추가합니다.
   * 엑셀 파일을 업로드하여 작업 공종을 등록합니다.
   * @returns {Promise<void>}
   */
  const addTypeOfWork = async () => {
    if (typeOfWorkSheetData.file) {
      const result = await addConstructionType({
        uuid: addTypeOfWorkModal.uuid,
        type: addTypeOfWorkModal.type,
        sheetName: typeOfWorkSheetData.name,
        excelFile: typeOfWorkSheetData.file,
        setIsLoading,
        setErrorModal,
        userInfo,
      });
      if (result) {
        setAddTypeOfWorkWarningModal(false);
        setAddTypeOfWorkModal({uuid: '', type: ''});
        setTypeOfWorkSheetData({name: '', file: undefined});
      }
    }
  };

  return (
    <ScreenLayout isLoading={isLoading} errorModal={errorModal}>
      <Container>
        <Inner>
          {/* 시공사 리스트 */}
          <DefaultText
            text="contractor"
            textStyle={{
              fontSize: minmax(30),
              fontWeight: 'bold',
              borderBottom: `3px solid ${colors.primary}`,
              width: 'fit-content',
              marginBottom: minmax(20),
            }}
          />
          <ResultArea>
            <ResultHeader>
              <DefaultText
                text=""
                textStyle={{
                  ...ResultHeaderTextStyle,
                  flex: 0.4,
                }}
              />
              <DefaultText
                text="registerDate"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText text="applicant" textStyle={ResultHeaderTextStyle} />
              <DefaultText
                text="applicantNumber"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText
                text="constructionCompanyName"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText
                text="coreCompanyAddress"
                textStyle={{...ResultHeaderTextStyle, flex: 2}}
              />
              <DefaultText
                text="businessLicense"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText
                text="businessRegistrationNumber"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText
                text="DUNS"
                textStyle={{...ResultHeaderTextStyle, flex: 0.5}}
              />
              <DefaultText text="state" textStyle={ResultHeaderTextStyle} />
              <DefaultText
                text="typeOfwork"
                textStyle={ResultHeaderTextStyle}
              />
            </ResultHeader>

            <ResultList>
              {contractorData?.totalCount ? (
                contractorData.contractorList.map(
                  (item: any, index: number) => (
                    <ResultItem
                      key={index}
                      onClick={() => {
                        //
                      }}>
                      <DefaultText
                        text={(
                          (contractorCurrentPage - 1) * 10 +
                          index +
                          1
                        ).toString()}
                        textStyle={{...ResultHeaderTextStyle, flex: 0.4}}
                      />
                      <DefaultText
                        text={moment(item.createdAt).format('YY-MM-DD')}
                        textStyle={ResultItemStyle}
                      />
                      <DefaultText
                        text={item.owner.profile.name}
                        textStyle={ResultItemStyle}
                      />
                      <DefaultText
                        text={formatPhoneNumber({
                          phone: item.owner.phone,
                          locale: item.owner.locale,
                        })}
                        textStyle={{...ResultItemStyle, flex: 1.5}}
                      />
                      <DefaultText
                        text={item.name}
                        textStyle={{...ResultItemStyle, flex: 1.5}}
                      />
                      <DefaultText
                        text={
                          item.address
                            ? item.address +
                              (item.addressDetail
                                ? ', ' + item.addressDetail
                                : '')
                            : '-'
                        }
                        textStyle={{...ResultItemStyle, flex: 2}}
                      />
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          flex: 1,
                        }}>
                        {item.businessRegisterNumberImg ? (
                          <DefaultText
                            text={'toConfirm'}
                            textStyle={{
                              width: 'fit-content',
                              cursor: 'pointer',
                              backgroundColor: colors.primary2,
                              color: colors.white,
                              borderRadius: minmax(10),
                              padding: minmax(10),
                            }}
                            onClick={() =>
                              setSelectItem({...item, type: 'contractors'})
                            }
                          />
                        ) : (
                          <FileInputButton
                            title="등록하기"
                            labelStyle={{
                              width: 'fit-content',
                              padding: minmax(10),
                              backgroundColor: colors.darkGray,
                            }}
                            setValue={async (file: File) => {
                              const newFile = (
                                file
                                  ? file.type.includes('pdf')
                                    ? await convertPdfToImage(file)
                                    : file.type.includes('image')
                                    ? file
                                    : null
                                  : null
                              ) as File | null;
                              // 변경 api 호출
                              if (newFile) {
                                const result =
                                  await changeBusinessRegisterNumberImg({
                                    userInfo,
                                    setErrorModal,
                                    setIsLoading,
                                    contractorUUID: item.uuid,
                                    businessRegisterNumberImg: newFile,
                                  });
                                if (result)
                                  setModal('changeBusinessRegisterNumberImg');
                              }
                            }}
                            active
                            accept="image/*, .pdf"
                          />
                        )}
                      </div>
                      <DefaultText
                        text={formatBusinessRegisterNumber(
                          item.businessRegisterNumber,
                        )}
                        textStyle={{...ResultItemStyle, flex: 1.5}}
                      />
                      <DefaultText
                        text={item.dunsNumber || '-'}
                        textStyle={{...ResultItemStyle, flex: 0.5}}
                      />

                      <DefaultText
                        text={allowStateStatus[item.allowState].text}
                        textStyle={{
                          ...ResultItemStyle,
                          color: allowStateStatus[item.allowState].color,
                          cursor: 'pointer',
                        }}
                        onClick={() =>
                          setSelectItem({
                            ...item,
                            type: 'contractors',
                            isState: true,
                          })
                        }
                      />
                      <div
                        style={{
                          flex: 1,
                          display: 'flex',
                          justifyContent: 'center',
                        }}>
                        <DefaultButton
                          text={'add'}
                          buttonStyle={{
                            width: 'fit-content',
                            backgroundColor: colors.primary2,
                            height: minmax(40),
                          }}
                          textStyle={{color: colors.white}}
                          onClick={() => {
                            setAddTypeOfWorkModal({
                              uuid: item.uuid,
                              type: 'contractors',
                            });
                          }}
                        />
                      </div>
                    </ResultItem>
                  ),
                )
              ) : (
                <DefaultText
                  text="noDataMsgTongList"
                  textStyle={{
                    color: colors.darkGray,
                    textAlign: 'center',
                    margin: `${minmax(12)} 0`,
                  }}
                />
              )}
            </ResultList>
          </ResultArea>
          {contractorData?.totalCount ? (
            <Pagination
              totalPage={contractorTotalPage}
              currentPage={contractorCurrentPage}
              setCurrentPage={setContractorCurrentPage}
            />
          ) : null}

          {/* 현장 리스트 - 사용X / 임시 복원 */}
          <DefaultText
            text="tongPlace"
            textStyle={{
              fontSize: minmax(30),
              fontWeight: 'bold',
              borderBottom: `3px solid ${colors.primary}`,
              width: 'fit-content',
              marginBottom: minmax(20),
            }}
          />
          <ResultArea>
            <ResultHeader>
              <DefaultText
                text=""
                textStyle={{
                  ...ResultHeaderTextStyle,
                  flex: 0.4,
                }}
              />
              <DefaultText
                text="registerDate"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText text="applicant" textStyle={ResultHeaderTextStyle} />
              <DefaultText
                text="applicantNumber"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText
                text="siteName"
                textStyle={{...ResultHeaderTextStyle, flex: 2}}
              />
              <DefaultText
                text="siteAddress"
                textStyle={{...ResultHeaderTextStyle, flex: 2}}
              />
              <DefaultText
                text="businessLicense"
                textStyle={ResultHeaderTextStyle}
              />
              <DefaultText
                text="businessRegistrationNumber"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText
                text="DUNS"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText
                text="onsitePhoneNumber"
                textStyle={{...ResultHeaderTextStyle, flex: 1.5}}
              />
              <DefaultText text="state" textStyle={ResultHeaderTextStyle} />
              <DefaultText
                text="typeOfwork"
                textStyle={ResultHeaderTextStyle}
              />
            </ResultHeader>

            <ResultList>
              {tongData?.totalCount ? (
                tongData.tongPlaceList.map((item, index: number) => (
                  <ResultItem
                    key={item.uuid}
                    onClick={() => {
                      //
                    }}>
                    <DefaultText
                      text={((tongCurrentPage - 1) * 10 + index + 1).toString()}
                      textStyle={{...ResultHeaderTextStyle, flex: 0.4}}
                    />
                    <DefaultText
                      text={moment(item.createdAt).format('YY-MM-DD')}
                      textStyle={ResultItemStyle}
                    />
                    <DefaultText
                      text={item.owner.profile?.name || '-'}
                      textStyle={ResultItemStyle}
                    />
                    <DefaultText
                      text={formatPhoneNumber({
                        phone: item.owner.phone,
                        locale: item.owner.locale,
                      })}
                      textStyle={{...ResultItemStyle, flex: 1.5}}
                    />
                    <DefaultText
                      text={item.name}
                      textStyle={{...ResultItemStyle, flex: 2}}
                    />
                    <DefaultText
                      text={
                        item.address +
                        (item.addressDetail ? ', ' + item.addressDetail : '')
                      }
                      textStyle={{...ResultItemStyle, flex: 2}}
                    />
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flex: 1,
                      }}>
                      {item.businessRegisterNumberImg ? (
                        <DefaultText
                          text={'확인하기'}
                          textStyle={{
                            width: 'fit-content',
                            cursor: 'pointer',
                            backgroundColor: colors.primary2,
                            color: colors.white,
                            borderRadius: minmax(10),
                            padding: minmax(10),
                          }}
                          onClick={() =>
                            setSelectItem({...item, type: 'tong-places'})
                          }
                        />
                      ) : (
                        '-'
                      )}
                    </div>
                    <DefaultText
                      text={formatBusinessRegisterNumber(
                        item.businessRegisterNumber,
                      )}
                      textStyle={{...ResultItemStyle, flex: 1.5}}
                    />
                    <DefaultText
                      text={item.dunsNumber || '-'}
                      textStyle={{...ResultItemStyle, flex: 1.5}}
                    />
                    <DefaultText
                      text={formatPhoneNumber({
                        phone: item.tongPlaceNumber,
                        locale: item.locale,
                      })}
                      textStyle={{...ResultItemStyle, flex: 1.5}}
                    />
                    <DefaultText
                      text={siteProcessingStatus[item.state].text}
                      textStyle={{
                        ...ResultItemStyle,
                        color: siteProcessingStatus[item.state].color,
                        cursor: 'pointer',
                      }}
                      onClick={() =>
                        setSelectItem({
                          ...item,
                          type: 'tong-places',
                          isState: true,
                        })
                      }
                    />

                    <div
                      style={{
                        flex: 1,
                        display: 'flex',
                        justifyContent: 'center',
                      }}>
                      <DefaultButton
                        text={'add'}
                        buttonStyle={{
                          width: 'fit-content',
                          backgroundColor: colors.primary2,
                          height: minmax(40),
                        }}
                        textStyle={{color: colors.white}}
                        onClick={() => {
                          setAddTypeOfWorkModal({
                            uuid: item.uuid,
                            type: 'tong-places',
                          });
                        }}
                      />
                    </div>
                  </ResultItem>
                ))
              ) : (
                <DefaultText
                  text="noDataMsgTongList"
                  textStyle={{
                    color: colors.darkGray,
                    textAlign: 'center',
                    margin: `${minmax(12)} 0`,
                  }}
                />
              )}
            </ResultList>
          </ResultArea>

          {tongData?.totalCount ? (
            <Pagination
              totalPage={tongTotalPage}
              currentPage={tongCurrentPage}
              setCurrentPage={setTongCurrentPage}
            />
          ) : null}
        </Inner>
      </Container>

      {modal !== '' && (
        <CommonModal
          title={modalData[modal].title}
          detail={modalData[modal].detail}
          firstButtonText={modalData[modal].firstButtonText}
          firstButtonStyle={modalData[modal].firstButtonStyle}
          secondButtonText={modalData[modal].secondButtonText}
          secondButtonStyle={modalData[modal].secondButtonStyle}
          firstButtonClick={modalData[modal].firstButtonClick}
          secondButtonClick={modalData[modal].secondButtonClick}
          closeModal={modalData[modal].firstButtonClick}
        />
      )}

      {/* 상태 변경 | 사업자등록증 모달 */}
      {selectItem &&
        (selectItem.isState ? (
          <ChangeStateModal
            selectItem={selectItem}
            closeModal={() => setSelectItem(null)}
            setIsLoading={setIsLoading}
            setErrorModal={setErrorModal}
          />
        ) : (
          <BusinessImgModal
            selectItem={selectItem}
            closeModal={() => setSelectItem(null)}
            setIsLoading={setIsLoading}
            setErrorModal={setErrorModal}
          />
        ))}

      {/* 작업 공종 추가 모달 */}
      {addTypeOfWorkModal.uuid && (
        <CommonModal>
          <div
            style={{
              minWidth: minmax(600),
              display: 'flex',
              flexDirection: 'column',
              rowGap: minmax(30),
            }}>
            <TitleText>{'작업 공종 추가하기'}</TitleText>
            <LabelInput
              type={'text'}
              labelText={'시트명'}
              value={typeOfWorkSheetData.name}
              setValue={(e: string) =>
                setTypeOfWorkSheetData(prev => ({...prev, name: e}))
              }
            />
            <FileInputButton
              title={'엑셀 파일 등록'}
              setValue={(e: any) => {
                setTypeOfWorkSheetData(prev => ({...prev, file: e}));
              }}
              active={true}
              accept=".xls,.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
              labelStyle={{
                backgroundColor: typeOfWorkSheetData.file
                  ? colors.primary2
                  : colors.gray,
                height: minmax(70),
              }}
            />
            <DefaultText text={typeOfWorkSheetData?.file?.name} />
            <DefaultText
              text={'1,500줄 이하의 엑셀 파일만 업로드해주세요.'}
              textStyle={{textAlign: 'end', color: colors.gypsyRed}}
            />
            <ButtonContainer>
              <DefaultButton
                text={'취소'}
                onClick={() => {
                  setAddTypeOfWorkModal({uuid: '', type: ''});
                  setTypeOfWorkSheetData({name: '', file: undefined});
                }}
              />
              <DefaultButton
                text={'확인'}
                buttonStyle={{backgroundColor: colors.primary2}}
                textStyle={{color: colors.white}}
                onClick={() => {
                  if (typeOfWorkSheetData.file && typeOfWorkSheetData.name)
                    setAddTypeOfWorkWarningModal(true);
                }}
              />
            </ButtonContainer>
          </div>
        </CommonModal>
      )}

      {/* 작업 공종 엑셀 파일 경고 모달 */}
      {addTypeOfWorkWarningModal && (
        <CommonModal>
          <WarningModalContainer>
            <DefaultText
              text="엑셀 파일을 업로드하면 되돌릴 수 없습니다."
              textStyle={{
                color: colors.gypsyRed,
                fontSize: minmax(32),
              }}
            />
            <DefaultText
              text="작업 공종을 추가하시겠습니까?"
              textStyle={{
                color: colors.gypsyRed,
                fontSize: minmax(32),
              }}
            />
            <ButtonContainer>
              <DefaultButton
                text={'취소'}
                onClick={() => setAddTypeOfWorkWarningModal(false)}
              />
              <DefaultButton
                text={'확인'}
                buttonStyle={{backgroundColor: colors.primary2}}
                textStyle={{color: colors.white}}
                onClick={async () => await addTypeOfWork()}
              />
            </ButtonContainer>
          </WarningModalContainer>
        </CommonModal>
      )}
    </ScreenLayout>
  );
};

export default Main;

const Container = styled.div`
  display: flex;
  width: 100%;
  flex: 1;
  flex-direction: column;
  margin-bottom: ${minmax(50)};
`;

const Inner = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: ${minmax(20)};
`;

const ResultArea = styled.div`
  margin-bottom: ${minmax(40)};
  border-bottom: 1px solid ${colors.darkGray};
`;

const ResultHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  height: ${minmax(52)};
  background-color: #f7f7f7;
`;

const ResultList = styled.ul`
  /* height: 728px; */
  /* overflow-y: auto; */

  // 스크롤바 삭제
  -ms-overflow-style: none; /* IE and Edge */
  scrollbar-width: none; /* Firefox */
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
`;

const ResultItem = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${minmax(8)} 0;
  min-height: ${minmax(52)};
  border-top: 1px solid ${colors.lightGrey};
  &:last-child {
    border-bottom: none;
  }
`;

const ResultHeaderTextStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  color: '#585858',
};

const ResultItemStyle: React.CSSProperties = {
  flex: 1,
  textAlign: 'center',
  // overflow: 'hidden',
  // textOverflow: 'ellipsis',
  // whiteSpace: 'nowrap',
  // wordBreak: 'break-all',
};

const ButtonContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
`;

const TitleText = styled.p`
  font-size: max(18px, min(2vw, 36px));
  font-weight: 500;
  letter-spacing: -1.08px;
  color: ${colors.black};
  margin-bottom: max(28px, min(2vw, 34px));
  text-align: center;
`;

const WarningModalContainer = styled.div`
  min-width: minmax(600);
  display: flex;
  flex-direction: column;
  row-gap: minmax(30);
  justify-content: center;
  align-items: center;
`;
